import { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "../locale";
import { slottingErrorText } from "../lib/slotting-error";
import {
  fetchBookingPage,
  submitBooking,
  submitRequest,
  type BookingPageData,
  type AvailableSlot,
} from "../../lib/slottingApi";
import { localTimeToIso } from "../../lib/utils/datetime-tz";
import { formatDateOnly, dateKeyOf } from "../lib/offering-date";
import { useEventTimezone } from "../theme/WBThemeRuntimeProvider";

export function SlottingBooking({ offeringId, theme, showAvailableCount, buttonText }: { offeringId?: string; theme?: 'light' | 'dark'; showAvailableCount?: boolean; buttonText?: string }) {
  const [data, setData] = useState<BookingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTreatment, setSelectedTreatment] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [companions, setCompanions] = useState<string[]>([]);
  const [showCompanions, setShowCompanions] = useState(false);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState<Set<string>>(new Set());
  const [availabilityWindows, setAvailabilityWindows] = useState<{ date: string; startTime: string; endTime: string }[]>([{ date: "", startTime: "", endTime: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const eventTz = useEventTimezone();
  const offeringTz = data?.offering?.timezone || eventTz;

  useEffect(() => {
    let active = true;
    if (!offeringId) {
      setLoading(false);
      return;
    }
    fetchBookingPage(offeringId)
      .then((d) => {
        if (!active) return;
        setData(d);
        if (d.treatments.length === 1 && d.treatments[0]) setSelectedTreatment(d.treatments[0].treatmentId);
      })
      .catch((err) => { if (!active) return; setError(slottingErrorText(err, "slottingFailedToLoad")); })
      .finally(() => { if (!active) return; setLoading(false); });
    return () => { active = false; };
  }, [offeringId]);

  const availableDates = useMemo(() => {
    if (!data) return [];
    const dates = new Set<string>();
    for (const slot of data.availableSlots) {
      // availableSlots now includes exhausted (spotsLeft: 0) slots for
      // the calendar's "Full" state — never offer them in the picker.
      if (slot.spotsLeft <= 0) continue;
      // Offering-zone calendar day (audit 2026-09-23, 4d) — the UTC prefix
      // filed a morning slot east of UTC under the previous day.
      dates.add(dateKeyOf(slot.startDt, offeringTz));
    }
    return Array.from(dates).sort();
  }, [data, offeringTz]);

  const filteredSlots = useMemo(() => {
    if (!data || !selectedDate) return [];
    const compatibleLaneIds = new Set<string>();
    if (selectedTreatment) {
      for (const lane of data.lanes) {
        if (!lane.treatmentIds?.length || lane.treatmentIds.includes(selectedTreatment)) {
          compatibleLaneIds.add(lane.laneId);
        }
      }
    } else {
      for (const lane of data.lanes) compatibleLaneIds.add(lane.laneId);
    }
    return data.availableSlots.filter(
      (s) =>
        s.spotsLeft > 0 &&
        dateKeyOf(s.startDt, offeringTz) === selectedDate &&
        compatibleLaneIds.has(s.laneId),
    );
  }, [selectedDate, selectedTreatment, data, offeringTz]);

  const maxCompanions = useMemo(() => {
    if (!selectedSlot || !data) return 0;
    const treatment = selectedTreatment
      ? data.treatments.find((t) => t.treatmentId === selectedTreatment)
      : undefined;
    const capacity = treatment?.capacity ?? 1;
    return Math.max(0, Math.min(capacity - 1, selectedSlot.spotsLeft - 1));
  }, [selectedSlot, selectedTreatment, data]);

  const isRequestBased = data?.offering.bookingMode === "REQUEST_BASED";
  const validWindows = availabilityWindows.filter((w) => w.date && w.startTime && w.endTime);

  const canSubmit = isRequestBased
    ? selectedTreatmentIds.size > 0 && validWindows.length > 0 && !submitting
    : selectedSlot !== null && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!offeringId) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      if (isRequestBased) {
        const windows = validWindows.map((w) => ({
          startDt: localTimeToIso(w.date, w.startTime, offeringTz),
          endDt: localTimeToIso(w.date, w.endTime, offeringTz),
        }));
        await submitRequest(offeringId, {
          treatmentIds: [...selectedTreatmentIds],
          availability: windows,
          companions: companions.filter((c) => c.trim() !== ""),
        });
        setRequestSubmitted(true);
      } else {
        if (!selectedSlot) return;
        const result = await submitBooking(offeringId, {
          ...(selectedTreatment ? { treatmentId: selectedTreatment } : {}),
          laneId: selectedSlot.laneId,
          startDt: selectedSlot.startDt,
          endDt: selectedSlot.endDt,
          companions: companions.filter((c) => c.trim() !== ""),
        });
        if (result.appointmentId) {
          const url = new URL(window.location.href);
          url.searchParams.set("appointmentId", result.appointmentId);
          window.history.replaceState({}, "", url.toString());
          window.dispatchEvent(new Event("popstate"));
        } else {
          setRequestSubmitted(true);
        }
      }
    } catch (err) {
      setSubmitError(slottingErrorText(err, "slottingBookingFailed"));
    } finally {
      setSubmitting(false);
    }
  }, [selectedSlot, offeringId, selectedTreatment, companions, isRequestBased, selectedTreatmentIds, validWindows]);

  // Immediately BELOW the last hook and ABOVE the loading/error/submitted
  // branches — i.e. the same position in the branch order it had before, just
  // after the hooks instead of before them. `handleSubmit`'s useCallback holds a
  // slot in React's hook list, so returning ahead of it ran one hook fewer than
  // the previous render and React threw "Rendered fewer hooks than expected" the
  // moment a loaded offering turned out to have no bookingMode.
  if (data && !data.offering.bookingMode) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b" }}>
        {t("slottingBookingUnavailable")}
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
        <div style={{ width: 20, height: 20, border: "2px solid #ddd", borderTopColor: "#121212", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0", color: "#64748b" }}>
        {error || t("slottingBookingUnavailable")}
      </div>
    );
  }

  if (requestSubmitted) {
    return (
      <div style={{ textAlign: "center", padding: "48px 16px", border: "1px solid #e2e8f0", borderRadius: 12 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>&#10003;</div>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{t("slottingRequestSubmitted")}</h2>
        <p style={{ color: "#64748b" }}>{t("slottingRequestSubmittedDesc")}</p>
      </div>
    );
  }

  return (
    <div className={theme === 'dark' ? 'dark' : ''} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Offering header */}
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>{data.offering.name}</h2>
        {data.offering.description && (
          <p style={{ color: "#64748b", fontSize: 14 }}>{data.offering.description}</p>
        )}
      </div>

      {isRequestBased ? (
        <>
          {/* Treatment multi-select */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>{t("slottingSelectTreatments")}</h3>
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
              {data.treatments.map((tr) => {
                const checked = selectedTreatmentIds.has(tr.treatmentId);
                return (
                  <button
                    key={tr.treatmentId}
                    onClick={() => {
                      setSelectedTreatmentIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(tr.treatmentId)) next.delete(tr.treatmentId);
                        else next.add(tr.treatmentId);
                        return next;
                      });
                    }}
                    style={{
                      textAlign: "left", padding: 16, borderRadius: 8, cursor: "pointer",
                      border: checked ? "2px solid #2563eb" : "1px solid #e2e8f0",
                      background: checked ? "rgba(37,99,235,0.05)" : "transparent",
                      display: "flex", alignItems: "center", gap: 12,
                    }}
                  >
                    <span style={{
                      width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                      border: checked ? "2px solid #2563eb" : "2px solid #cbd5e1",
                      background: checked ? "#2563eb" : "transparent",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "#fff", fontSize: 12,
                    }}>
                      {checked ? "\u2713" : ""}
                    </span>
                    <div>
                      <div style={{ fontWeight: 500 }}>{tr.name}</div>
                      <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{tr.durationMinutes} min</div>
                      {tr.description && <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{tr.description}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Availability windows */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>{t("slottingYourAvailability")}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {availabilityWindows.map((w, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <label style={{ fontSize: 13, color: "#64748b", minWidth: 40 }}>{t("slottingDateLabel")}</label>
                  <input
                    type="date"
                    value={w.date}
                    onChange={(e) => {
                      const next = [...availabilityWindows];
                      next[i] = { ...w, date: e.target.value };
                      setAvailabilityWindows(next);
                    }}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, outline: "none" }}
                  />
                  <label style={{ fontSize: 13, color: "#64748b", minWidth: 24 }}>{t("slottingStartTime")}</label>
                  <input
                    type="time"
                    value={w.startTime}
                    onChange={(e) => {
                      const next = [...availabilityWindows];
                      next[i] = { ...w, startTime: e.target.value };
                      setAvailabilityWindows(next);
                    }}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, outline: "none" }}
                  />
                  <label style={{ fontSize: 13, color: "#64748b", minWidth: 24 }}>{t("slottingEndTime")}</label>
                  <input
                    type="time"
                    value={w.endTime}
                    onChange={(e) => {
                      const next = [...availabilityWindows];
                      next[i] = { ...w, endTime: e.target.value };
                      setAvailabilityWindows(next);
                    }}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, outline: "none" }}
                  />
                  {availabilityWindows.length > 1 && (
                    <button
                      onClick={() => setAvailabilityWindows(availabilityWindows.filter((_, j) => j !== i))}
                      style={{ padding: "4px 8px", fontSize: 14, color: "#64748b", background: "none", border: "none", cursor: "pointer" }}
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => setAvailabilityWindows([...availabilityWindows, { date: "", startTime: "", endTime: "" }])}
                style={{ fontSize: 14, color: "#2563eb", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textAlign: "left", padding: 0 }}
              >
                + {t("slottingAddWindow")}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Treatment selector */}
          {data.treatments.length > 1 && (
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>{t("slottingSelectService")}</h3>
              <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
                {data.treatments.map((tr) => (
                  <button
                    key={tr.treatmentId}
                    onClick={() => { setSelectedTreatment(tr.treatmentId); setSelectedSlot(null); }}
                    style={{
                      textAlign: "left", padding: 16, borderRadius: 8,
                      border: selectedTreatment === tr.treatmentId ? "2px solid #2563eb" : "1px solid #e2e8f0",
                      background: selectedTreatment === tr.treatmentId ? "rgba(37,99,235,0.05)" : "transparent",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{tr.name}</div>
                    <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{tr.durationMinutes} min</div>
                    {tr.description && <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>{tr.description}</div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date picker */}
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>{t("slottingSelectDate")}</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {availableDates.length === 0 ? (
                <p style={{ color: "#64748b" }}>{t("slottingNoAvailableDates")}</p>
              ) : (
                availableDates.map((date) => (
                  <button
                    key={date}
                    onClick={() => { setSelectedDate(date); setSelectedSlot(null); }}
                    style={{
                      padding: "8px 16px", borderRadius: 8, fontSize: 14, cursor: "pointer",
                      border: selectedDate === date ? "2px solid #2563eb" : "1px solid #e2e8f0",
                      background: selectedDate === date ? "rgba(37,99,235,0.05)" : "transparent",
                    }}
                  >
                    {formatDateOnly(date, undefined, { weekday: "short", month: "short", day: "numeric" })}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Time slots */}
          {selectedDate && (
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>{t("slottingAvailableTimes")}</h3>
              {filteredSlots.length === 0 ? (
                <p style={{ color: "#64748b" }}>{t("slottingNoAvailableTimes")}</p>
              ) : (
                <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
                  {filteredSlots.map((slot, i) => {
                    const start = new Date(slot.startDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" });
                    const end = new Date(slot.endDt).toLocaleTimeString(undefined, { timeZone: offeringTz, hour: "2-digit", minute: "2-digit" });
                    const isSelected = selectedSlot?.startDt === slot.startDt && selectedSlot?.laneId === slot.laneId;
                    return (
                      <button
                        key={`${slot.laneId}-${slot.startDt}-${i}`}
                        onClick={() => setSelectedSlot(slot)}
                        style={{
                          padding: 12, borderRadius: 8, textAlign: "left", fontSize: 14, cursor: "pointer",
                          border: isSelected ? "2px solid #2563eb" : "1px solid #e2e8f0",
                          background: isSelected ? "rgba(37,99,235,0.05)" : "transparent",
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{start} &ndash; {end}</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                          {slot.laneName}{showAvailableCount !== false ? ` \u00b7 ${slot.spotsLeft} spot${slot.spotsLeft !== 1 ? "s" : ""} left` : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Companions */}
      {maxCompanions > 0 && (
        <div>
          <button
            onClick={() => setShowCompanions(!showCompanions)}
            style={{ fontSize: 14, color: "#2563eb", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
          >
            {showCompanions ? t("slottingHideCompanions") : t("slottingAddCompanions")}
          </button>
          {showCompanions && (
            <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              {companions.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text" value={c}
                    onChange={(e) => { const next = [...companions]; next[i] = e.target.value; setCompanions(next); }}
                    placeholder={t("slottingCompanionId", String(i + 1))}
                    style={{ flex: 1, padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 14, outline: "none" }}
                  />
                  <button
                    onClick={() => setCompanions(companions.filter((_, j) => j !== i))}
                    style={{ padding: "8px 12px", fontSize: 14, color: "#64748b", background: "none", border: "none", cursor: "pointer" }}
                  >{t("slottingRemove")}</button>
                </div>
              ))}
              {companions.length < maxCompanions && (
                <button
                  onClick={() => setCompanions([...companions, ""])}
                  style={{ fontSize: 14, color: "#2563eb", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                >{t("slottingAddCompanion")}</button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {submitError && (
        <div style={{ padding: 12, borderRadius: 8, background: "rgba(239,68,68,0.1)", color: "#dc2626", fontSize: 14 }}>
          {submitError}
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{
          width: "100%", padding: 12, borderRadius: 8, fontWeight: 500, fontSize: 14, border: "none", cursor: canSubmit ? "pointer" : "not-allowed",
          background: canSubmit ? "#2563eb" : "#e2e8f0",
          color: canSubmit ? "#ffffff" : "#94a3b8",
        }}
      >
        {submitting
          ? t("slottingSubmitting")
          : buttonText
            ? buttonText
            : data.offering.bookingMode === "REQUEST_BASED"
              ? t("slottingSubmitRequest")
              : t("slottingBookAppointment")}
      </button>
    </div>
  );
}
